import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';

import type { EntryListItem, SipConfigurationsData } from 'chaika-frontend-types/frontend-cms';
import type { Nullable, Optional } from 'chaika-common-types';

import type { RightMenuProps } from '@components/RightMenu';
import type { ContentNavItemParams } from '@components/ContentNav';
import type { InformPopupProps } from '@components/Popup/InformPopup';
import type { ConfirmPopupProps } from '@components/Popup/ConfirmPopup';
import type { SelectOrganizationPopupParams } from '@core/Page/SelectOrganizationPopup/SelectOrganizationPopup';

import { LeftMenu } from './LeftMenu';
import { HeaderView } from './Header';
import { RightMenu } from './RightMenu';
import { translate } from '@chaika/i18n/helper';
import { Overlay } from '@store/App/Overlay';
import { ClickOutsideMaskParams } from '@components/ClickOutsideMask/ClickOutsideMask';
import { assertIsDefined } from '@utils/assertIsDefined';

export const APP_DEFAULT_URL: string = '/';

export interface SimplePopupParams extends Omit<InformPopupProps, 'onClose'> {
    onClose?(): void;
}

export interface ConfirmPopupParams extends Omit<ConfirmPopupProps, 'onClose' | 'onCancel'> {
    onClose?(): void;
    onCancel?(): void;
}

interface PhoneWidgetParams {
    isOpen: boolean;
    initialPhoneNumber?: string;
    hasActiveSession: boolean;
}

interface DoOrShowErrorParams<T> {
    errorMessage?: string;
    closeText?: string;
    showLoader?: boolean;
    description?: string;
    allowMultiAction?: boolean;
    action(): Promise<T> | T;
    errorHandler?(e: any): Promise<void> | void;
    onError?(error: any): void;
}

export interface ExistingReferralPopupParams {
    sameMedicalCase?: boolean;
    title: string;
    serviceName?: string;
    caseName?: string;
    doctorName?: string;
    onAddClick?(): void;
    onCreateClick(): void;
    onCancel(): void;
}

export interface ExistingEntryPopupParams {
    todayEntries: EntryListItem[];
    openEntry(id: string): void;
    onConfirm(): void;
}

export class AppState {
    public static Name = 'app' as const;

    @observable public previousUrl: string = APP_DEFAULT_URL;
    @observable public forbiddenResources = false;
    @observable public phoneWidget: PhoneWidgetParams = {
        isOpen: false,
        initialPhoneNumber: '',
        hasActiveSession: false,
    };

    @observable public greyBackground = false;
    @observable public whiteHeaderBackground = false;
    @observable public isOnUpdate = false;
    @observable public isOnLoad = false;
    @observable public showLoader = false;
    @observable public rootFixed = false;
    @observable public selectOrganizationPopupParams: Optional<SelectOrganizationPopupParams>;
    @observable public sip?: SipConfigurationsData;
    @observable public airtableAddService?: string;
    @observable public airtableChangePrice?: string;
    @observable public site?: string;
    @observable public isSupportEnabled = false;
    @observable public isOnValidation = false;
    @observable public saveRequestIds: string[] = [];
    @observable public headerView: HeaderView = new HeaderView();
    @observable public withoutHeader = false;
    @observable public withoutCustomScroll = false;
    @observable public hideScrollBar = false;
    @observable public withoutLeftMenu = false;
    @observable public withPaddingTop = true;
    @observable public withPaddingBottom = true;
    @observable public leftMenu: LeftMenu = new LeftMenu();
    @observable public showingPopupParams: Nullable<SimplePopupParams> = null;
    @observable public confirmPopupParams: Nullable<ConfirmPopupParams> = null;
    @observable public rightMenu: RightMenu = new RightMenu();
    @observable public appOverlay?: Overlay;
    @observable public withContentNavigation: boolean = false;
    @observable public contentNavigationItems: ContentNavItemParams[] = [];
    @observable public existingReferralPopup?: ExistingReferralPopupParams = undefined;
    @observable public pageScrollHandler?: (e: React.UIEvent<HTMLDivElement>) => void = undefined;
    @observable public scrollBarRef?: React.RefObject<HTMLDivElement | null> = React.createRef();
    @observable private onDoOrShowErrorNames = new Set<string>();

    constructor() {
        makeObservable(this, undefined, { autoBind: true });
    }

    @computed
    public get isOnSave(): boolean {
        return this.saveRequestIds.length > 0;
    }

    @computed
    public get sipConfigLoaded(): boolean {
        return !!this.sip;
    }

    @computed
    public get showingPopupParamsSet(): boolean {
        return !!this.showingPopupParams;
    }

    @computed
    public get existingReferralPopupSet(): boolean {
        return !!this.existingReferralPopup;
    }

    @computed
    public get headerWithBottomLine(): boolean {
        return !!this.headerView.bottomContent;
    }

    @computed
    public get confirmPopupParamsSet(): boolean {
        return !!this.confirmPopupParams;
    }

    @action
    public reset(): void {
        this.headerView.reset();
        this.rightMenu.reset();
        this.leftMenu.disableOpen = false;
        this.contentNavigationItems = [];
    }

    @action
    public setOverlay(params: ClickOutsideMaskParams): void {
        this.appOverlay = new Overlay(params);
    }

    @action
    public setGreyBackground(value: boolean): void {
        this.greyBackground = value;
    }

    @action
    public setRightMenu(params: RightMenuProps): void {
        this.rightMenu.setRightMenu(params);
    }

    @action
    public setPreviousUrl(previousUrl: string): void {
        this.previousUrl = previousUrl;
    }

    @action.bound
    public resetRightMenu(): void {
        this.rightMenu.reset();
    }

    @action.bound
    public resetOverlay(): void {
        this.appOverlay = undefined;
    }

    @action
    public addSaveRequest(id: string): void {
        this.saveRequestIds.push(id);
    }

    @action
    public removeSaveRequest(removedId: string): void {
        this.saveRequestIds = this.saveRequestIds.filter(id => id !== removedId);
    }

    @action
    public showPopup(params: Nullable<SimplePopupParams>): void {
        this.showingPopupParams = params;
    }

    @action
    public showConfirmPopup(params: Nullable<ConfirmPopupParams>): void {
        this.confirmPopupParams = params;
    }

    @action
    public updateConfirmPopup(params: Nullable<Partial<ConfirmPopupParams>>): void {
        assertIsDefined(this.confirmPopupParams, 'Confirm popup is updated only while it is shown');
        this.confirmPopupParams = {
            ...this.confirmPopupParams,
            ...params,
        };
    }

    @action.bound
    public async doOrShowError<T>({
        action,
        errorHandler,
        onError,
        errorMessage,
        description,
        closeText,
        allowMultiAction,
        showLoader = true,
    }: DoOrShowErrorParams<T>): Promise<Optional<T>> {
        let result: Optional<T>;
        const key = action.toString();
        if (!this.onDoOrShowErrorNames.has(key) || allowMultiAction) {
            this.onDoOrShowErrorNames.add(key);
            this.isOnUpdate = showLoader;

            try {
                result = await action();
            } catch (e: any) {
                if (errorHandler) {
                    await errorHandler(e);
                } else {
                    console.error(e);
                    onError?.(e);
                    this.showPopup({
                        closeText,
                        title: errorMessage ?? translate('error'),
                        description: description ?? e?.message,
                    });
                }
            }

            this.isOnUpdate = false;
            this.onDoOrShowErrorNames.delete(key);
        }

        return result;
    }

    @action
    public setExistingReferralPopup(params?: ExistingReferralPopupParams): void {
        this.existingReferralPopup = params;
    }

    @action
    public setSelectOrganizationPopupParams(params: SelectOrganizationPopupParams): void {
        this.selectOrganizationPopupParams = params;
    }

    @action
    public resetSelectOrganizationPopupParams(): void {
        this.selectOrganizationPopupParams = undefined;
    }

    @action
    public setPageScrollHandler(handler: (e: React.UIEvent<HTMLDivElement>) => void): void {
        this.pageScrollHandler = handler;
    }

    @action
    public resetPageScrollHandler(): void {
        this.pageScrollHandler = undefined;
    }

    @action
    public setIsOnLoad(isOnLoad: boolean): void {
        this.isOnLoad = isOnLoad;
    }

    @action
    public setIsOnUpdate(isOnUpdate: boolean): void {
        this.isOnUpdate = isOnUpdate;
    }

    @action
    public setHeaderVisibility(visible: boolean): void {
        this.withoutHeader = !visible;
    }

    @action
    public setLeftMenuVisibility(visible: boolean): void {
        this.withoutLeftMenu = !visible;
    }

    @action
    public scrollTop(): void {
        const scrollBar = this.scrollBarRef?.current;
        if (scrollBar) scrollBar.scrollTo({ top: 0 });
    }

    @action
    public toggleTopPadding(withPadding: boolean): void {
        this.withPaddingTop = withPadding;
    }

    @action
    public toggleGreyBackground(withBackground: boolean): void {
        this.greyBackground = withBackground;
    }
}

export const app = new AppState();
